import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { SbButton, SbSelect, SbSpin, SbEmpty, SbTooltip } from "uiCore";
import {
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
  ActionRowStyled,
} from "styles/common.styles";
import { PageTransitionStyled } from "./talentMobility.animations";
import { BackLinkStyled } from "./RoleDetailV2.styles";
import {
  TreeContainerStyled,
  OrgChartListStyled,
  ChartScrollAreaStyled,
  ZoomControlsStyled,
  ZoomLevelStyled,
  FilterRowStyled,
  FilterActionsStyled,
  CenteredStateStyled,
  StateMessageStyled,
} from "./SuccessionPlanningV2.styles";
import OrgChartNode from "./components/OrgChartNode";
import { useSuccessionTreeFilterOptions, useSuccessionTree, EMPTY_SUCCESSION_FILTERS } from "./useSuccessionTree";
import { buildPositionForest, flattenAllEmployees, collectExpandablePositionKeys } from "./successionPositionGrouping.utils";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";
import { exportNodeAsPng } from "Utils/exportPng.utils";
import { TEXT } from "./talentMobility.constants";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.2;
const ZOOM_STEP = 0.1;
const ZOOM_DEFAULT = 1;

const FILTER_FIELDS = [
  { key: "legalEntity", label: "Legal Entity" },
  { key: "businessUnit", label: "Business Unit" },
  { key: "function", label: "Function" },
  { key: "subFunction", label: "Sub-Function" },
  { key: "level", label: "Level" },
];

const hasAnyFilter = (filters) => Object.values(filters).some((v) => v?.length > 0);

// Order-insensitive equality so re-applying the same selection (e.g. clicking
// Apply twice) doesn't trigger a redundant tree refetch.
const sortedFilters = (filters) =>
  Object.fromEntries(Object.entries(filters).map(([key, values]) => [key, [...(values || [])].sort()]));
const filtersEqual = (a, b) => JSON.stringify(sortedFilters(a)) === JSON.stringify(sortedFilters(b));

const ANALYTICS_FROM = "talent_mobility_succession_planning";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.SUCCESSION_PLANNING_VIEWED;

const SuccessionPlanningV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_SUCCESSION_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_SUCCESSION_FILTERS);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const [selectedEmployeeByPosition, setSelectedEmployeeByPosition] = useState({});
  const [zoom, setZoom] = useState(ZOOM_DEFAULT);
  const [isPanning, setIsPanning] = useState(false);
  const treeRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const panStateRef = useRef(null); // { startX, startY, startScrollLeft, startScrollTop } while dragging

  const {
    options: filterOptions,
    loading: filterOptionsLoading,
    error: filterOptionsError,
    refetch: refetchFilterOptions,
  } = useSuccessionTreeFilterOptions();

  const { roots, warnings, loading: treeLoading, error: treeError, refetch: refetchTree } = useSuccessionTree(appliedFilters);

  // One position node per organizational position — siblings sharing a
  // manager and a position (requisition_number, falling back to normalized
  // current_role) are grouped into one card with every occupant attached.
  // See successionPositionGrouping.utils for why this exists.
  const positionForest = useMemo(() => buildPositionForest(roots), [roots]);

  // A freshly-fetched tree starts fully expanded, with each position showing
  // its first occupant. Reset during render (not a useEffect) — an effect is
  // a passive, deferred callback, so a user interaction landing in the
  // window before it flushes can race with it and get silently overwritten
  // (observed as a click switching a position's employee, then immediately
  // reverting). Adjusting state directly during render, guarded by a ref to
  // the previous positionForest, applies synchronously in the same render
  // pass that produced the new tree — see https://react.dev/learn/you-might-not-need-an-effect#adjusting-state-when-a-prop-changes.
  const lastPositionForestRef = useRef(positionForest);
  if (lastPositionForestRef.current !== positionForest) {
    lastPositionForestRef.current = positionForest;
    setCollapsedKeys(new Set());
    setSelectedEmployeeByPosition({});
  }

  const setFilterField = (field) => (value) => setSelectedFilters((prev) => ({ ...prev, [field]: value || [] }));

  const handleApplyFilters = () => {
    setAppliedFilters((prev) => (filtersEqual(prev, selectedFilters) ? prev : { ...selectedFilters }));
  };

  const handleClearAllFilters = () => {
    setSelectedFilters(EMPTY_SUCCESSION_FILTERS);
    setAppliedFilters(EMPTY_SUCCESSION_FILTERS);
  };

  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +z.toFixed(2)));

  // Plain scale change — TreeContainerStyled's transform-origin is "top
  // center", so the chart always shrinks/grows toward its own horizontal
  // center rather than the top-left corner. That keeps it visually centered
  // at any zoom level without needing to track/compensate scroll position,
  // which is what made zooming feel like it was "jumping" before.
  const zoomIn = () => setZoom((z) => clampZoom(z + ZOOM_STEP));
  const zoomOut = () => setZoom((z) => clampZoom(z - ZOOM_STEP));
  const zoomReset = () => setZoom(ZOOM_DEFAULT);

  // Ctrl/Cmd+scroll (also how browsers report trackpad pinch-zoom) zooms —
  // plain scroll is left alone so normal wheel/trackpad panning still works.
  const handleWheelZoom = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault(); // stop the browser's own native page-zoom on Ctrl+wheel
    setZoom((z) => clampZoom(z + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)));
  };

  // React registers onWheel as a passive DOM listener, so e.preventDefault()
  // inside a JSX-bound handler is silently ignored by the browser — Ctrl+wheel
  // would still trigger the browser's own page zoom alongside ours. Attaching
  // a real listener with { passive: false } is the only way preventDefault
  // actually takes effect here. handleWheelZoomRef always holds the latest
  // closure (fresh `zoom`) so the DOM listener itself only needs to attach once.
  const handleWheelZoomRef = useRef(handleWheelZoom);
  handleWheelZoomRef.current = handleWheelZoom;

  useEffect(() => {
    const scrollEl = scrollAreaRef.current;
    if (!scrollEl) return undefined;
    const listener = (e) => handleWheelZoomRef.current(e);
    scrollEl.addEventListener("wheel", listener, { passive: false });
    return () => scrollEl.removeEventListener("wheel", listener);
  }, []);

  // Click-and-drag panning — starts on mousedown anywhere in the scroll area
  // except real interactive controls (buttons, the employee-picker toggle),
  // tracked via window-level listeners so dragging keeps working even once
  // the cursor leaves the chart's bounds.
  const handlePanStart = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button, a, [role='button'], input, select")) return;
    const scrollEl = scrollAreaRef.current;
    if (!scrollEl) return;
    panStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startScrollLeft: scrollEl.scrollLeft,
      startScrollTop: scrollEl.scrollTop,
    };
    setIsPanning(true);
  };

  useEffect(() => {
    if (!isPanning) return undefined;
    const handleMove = (e) => {
      const state = panStateRef.current;
      const scrollEl = scrollAreaRef.current;
      if (!state || !scrollEl) return;
      scrollEl.scrollLeft = state.startScrollLeft - (e.clientX - state.startX);
      scrollEl.scrollTop = state.startScrollTop - (e.clientY - state.startY);
    };
    const handleEnd = () => {
      panStateRef.current = null;
      setIsPanning(false);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
    };
  }, [isPanning]);

  // Every occupant of every position — independent of which occupant is
  // currently selected in any card's picker, so CSV export/counts never drop
  // a record.
  const allEmployees = useMemo(() => flattenAllEmployees(positionForest), [positionForest]);
  const expandablePositionKeys = useMemo(
    () => collectExpandablePositionKeys(positionForest, selectedEmployeeByPosition),
    [positionForest, selectedEmployeeByPosition]
  );

  const toggleNode = (positionKey) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(positionKey)) next.delete(positionKey);
      else next.add(positionKey);
      return next;
    });

  const selectEmployeeForPosition = (positionKey, employeeId) =>
    setSelectedEmployeeByPosition((prev) => ({ ...prev, [positionKey]: employeeId }));

  const handleExpandAll = () => setCollapsedKeys(new Set());
  const handleCollapseAll = () => setCollapsedKeys(new Set(expandablePositionKeys));

  const handleExportCsv = () => {
    const rows = allEmployees.map((node) => ({
      "Employee Name": node.employee_name,
      "Current Role": node.current_role || "",
      "Legal Entity": node.legal_entity || "",
      "Business Unit": node.business_unit || "",
      Function: node.function || "",
      "Sub-Function": node.sub_function || "",
      Level: node.level || "",
      Active: node.is_active === false ? "No" : "Yes",
      "Hierarchy Context Only": node.context_only ? "Yes" : "No",
    }));
    exportRowsAsCsv(rows, "succession-planning");
  };

  const handleExportPng = () => exportNodeAsPng(treeRef.current, "succession-planning-org-view");

  const isFiltered = hasAnyFilter(appliedFilters);
  const noRootsAtAll = !treeLoading && !treeError && roots.length === 0;

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <BackLinkStyled onClick={() => navigate("/talent-mobility")}>
            <ArrowLeftOutlined /> {TEXT.BACK_TO_TALENT_MOBILITY}
          </BackLinkStyled>
          <HeadingRowStyled>
            <Title level={4}>{TEXT.BTN_SUCCESSION_PLANNING} — Org View</Title>
          </HeadingRowStyled>
          <Text type="secondary">Create a succession plan for the roles within your Organization, BU, Function & Teams.</Text>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <ActionRowStyled>
            <SbTooltip content="Drag to pan · Ctrl/Cmd + Scroll to zoom">
              <ZoomControlsStyled>
                <SbButton buttonType="secondary" aria-label="Zoom out" icon={<ZoomOutOutlined />} onClick={zoomOut} disabled={zoom <= ZOOM_MIN} />
                <ZoomLevelStyled>{Math.round(zoom * 100)}%</ZoomLevelStyled>
                <SbButton buttonType="secondary" aria-label="Zoom in" icon={<ZoomInOutlined />} onClick={zoomIn} disabled={zoom >= ZOOM_MAX} />
                <SbButton buttonType="secondary" aria-label="Reset zoom" icon={<UndoOutlined />} onClick={zoomReset} disabled={zoom === ZOOM_DEFAULT} />
              </ZoomControlsStyled>
            </SbTooltip>
            <SbButton buttonType="secondary" onClick={handleExpandAll} disabled={!allEmployees.length}>
              Expand All
            </SbButton>
            <SbButton buttonType="secondary" onClick={handleCollapseAll} disabled={!expandablePositionKeys.length}>
              Collapse All
            </SbButton>
            <SbButton buttonType="secondary" icon={<FileExcelOutlined />} onClick={handleExportCsv} disabled={!allEmployees.length}>
              Export to CSV
            </SbButton>
            <SbButton buttonType="secondary" icon={<DownloadOutlined />} onClick={handleExportPng} disabled={!allEmployees.length}>
              Download as PNG
            </SbButton>
          </ActionRowStyled>
        </HeaderRightStyled>
      </PageHeaderStyled>

      <FilterRowStyled>
        {FILTER_FIELDS.map(({ key, label }) => (
          <div className="filter-item" key={key}>
            <span>{label}</span>
            <SbSelect
              size="small"
              mode="multiple"
              allowClear
              placeholder="All"
              aria-label={label}
              loading={filterOptionsLoading}
              disabled={filterOptionsLoading}
              value={selectedFilters[key]}
              options={filterOptions?.[key] || []}
              onChange={setFilterField(key)}
            />
          </div>
        ))}
        <FilterActionsStyled>
          {filterOptionsError && (
            <SbButton size="small" buttonType="secondary" icon={<ReloadOutlined />} onClick={refetchFilterOptions}>
              Retry Filters
            </SbButton>
          )}
          <SbButton
            size="small"
            buttonType="secondary"
            onClick={handleClearAllFilters}
            disabled={!hasAnyFilter(selectedFilters) && !hasAnyFilter(appliedFilters)}
          >
            Clear All
          </SbButton>
          <SbButton size="small" buttonType="primary" onClick={handleApplyFilters}>
            Apply Filters
          </SbButton>
        </FilterActionsStyled>
      </FilterRowStyled>

      <ChartScrollAreaStyled
        ref={scrollAreaRef}
        $isPanning={isPanning}
        onMouseDown={handlePanStart}
        data-testid="succession-chart-scroll-area"
      >
        {treeLoading && (
          <CenteredStateStyled>
            <SbSpin size="large" />
            <StateMessageStyled>Loading succession hierarchy…</StateMessageStyled>
          </CenteredStateStyled>
        )}

        {!treeLoading && treeError && (
          <CenteredStateStyled>
            <SbEmpty description="Unable to load the succession hierarchy." />
            <SbButton buttonType="primary" icon={<ReloadOutlined />} onClick={refetchTree}>
              Retry
            </SbButton>
          </CenteredStateStyled>
        )}

        {!treeLoading && !treeError && noRootsAtAll && (
          <CenteredStateStyled>
            <SbEmpty
              description={
                isFiltered
                  ? "No employees match the selected filters."
                  : warnings.length > 0
                  ? "Succession hierarchy data is currently unavailable due to a data-quality issue."
                  : "No succession hierarchy is available for your current access."
              }
            />
            {isFiltered && (
              <SbButton buttonType="secondary" onClick={handleClearAllFilters}>
                Clear Filters
              </SbButton>
            )}
          </CenteredStateStyled>
        )}

        {!treeLoading && !treeError && roots.length > 0 && (
          <TreeContainerStyled ref={treeRef} $zoom={zoom}>
            <OrgChartListStyled>
              {positionForest.map((positionNode) => (
                <OrgChartNode
                  key={positionNode.positionKey}
                  positionNode={positionNode}
                  selectedEmployeeByPosition={selectedEmployeeByPosition}
                  onSelectEmployee={selectEmployeeForPosition}
                  collapsedKeys={collapsedKeys}
                  onToggleCollapse={toggleNode}
                />
              ))}
            </OrgChartListStyled>
          </TreeContainerStyled>
        )}
      </ChartScrollAreaStyled>
    </PageTransitionStyled>
  );
};

export default SuccessionPlanningV2;
